/**
 * Turnos — quién está libre y a qué distancia de donde se necesita.
 *
 * Lo pidió Luis: «no veo nada que muestre los turnos de los conductores para
 * saber si están libres, y con GPS para saber distancia».
 *
 * 🏗️ La estructura de siempre: título → indicadores → UNA caja de filtros →
 * tabla. Nada entre los filtros y la tabla.
 *
 * ⭐ El filtro no es un filtro cualquiera: al escoger a dónde se necesita el
 * camión, la tabla **se reordena por cercanía**. Es la pregunta que hace un
 * despachador —«¿quién tengo cerca de Cartagena?»—, no «muéstrame los libres».
 */

import { useCallback, useEffect, useState } from 'react'
import { pedirConSesion } from './Entrar.tsx'
import { usarPaginas, PieDeTabla } from './Tabla.tsx'

interface Libre {
  id: string
  placa: string
  propiedad: string
  /** true = el conductor lo DIJO. false = lo deducimos de que no lleva carga. */
  confirmado: boolean
  /** Cuántas remesas le siguen figurando abiertas en Vulcano. */
  encima: number | string | null
  disponible_desde: string | null
  ultimo_viaje: string | null
  dias_quieto: number | null
  tercero: string | null
  conductor: string | null
  conductor_cedula: string | null
  destino_direccion: string | null
  /** Dónde terminó su último viaje, ya con nombre de municipio. */
  donde: string | null
  /** De dónde salió la ubicación: 'GPS' o 'ULTIMO_DESTINO'. */
  fuenteUbicacion: string | null
  km: string | number | null
  horas_libre: number | null
}

interface Destino {
  codigo: string
  nombre: string
  cuantas: number
}

interface Datos {
  libres: Libre[]
  resumen: Record<string, string>
  diasEnLaOperacion: number
  cerca: { codigo: string; nombre: string } | null
  destinos: Destino[]
}

interface QuienManejaba {
  placa: string
  fecha: string
  conductores: {
    cedula: string
    nombre: string | null
    remesas: number
    masCerca: number
    eseMismoDia: boolean
  }[]
  remesas: {
    remesa: string
    fecha_despacho: string
    conductor: string | null
    conductor_cedula: string
    destino: string | null
    numero_manifiesto: string | null
    ese_mismo_dia: boolean
    dias_de_diferencia: number
  }[]
  ventana: { antes: number; despues: number }
}

/**
 * «¿Quién manejaba esta placa este día?»
 *
 * 🚨 Resuelve algo que pasa de verdad: **llega un comparendo dos meses después
 * con la placa y la fecha**, o un cliente reclama por una entrega vieja, y hay
 * que saber quién iba manejando. Hoy eso se contesta llamando a preguntar.
 *
 * ⭐ La respuesta va con su RESPALDO: los viajes que esa placa hizo alrededor
 * de ese día. Un nombre sin con qué sustentarlo no sirve para contestarle a
 * nadie —ni a tránsito, ni a un cliente, ni a un abogado—.
 */
function QuienManejaba() {
  const [placa, setPlaca] = useState('')
  const [fecha, setFecha] = useState('')
  const [r, setR] = useState<QuienManejaba | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [error, setError] = useState('')

  async function buscar() {
    if (!placa.trim() || !fecha) return
    setBuscando(true)
    setError('')
    setR(null)
    try {
      setR(
        await pedirConSesion<QuienManejaba>(
          `/api/admin/quien-manejaba?placa=${encodeURIComponent(placa)}&fecha=${fecha}`,
        ),
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo buscar')
    } finally {
      setBuscando(false)
    }
  }

  const gana = r?.conductores[0]

  return (
    <>
      <div className="filtros-barra">
        <div className="campo">
          <label htmlFor="qm-placa">Placa del camión</label>
          <input
            id="qm-placa"
            className="num"
            placeholder="LZL701"
            value={placa}
            onChange={(e) => setPlaca(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && void buscar()}
          />
        </div>
        <div className="campo">
          <label htmlFor="qm-fecha">Qué día</label>
          <input
            id="qm-fecha"
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
        </div>
        <button className="btn-tabla btn-tabla-fuerte" onClick={() => void buscar()} disabled={buscando}>
          {buscando ? 'Buscando…' : 'Buscar'}
        </button>
      </div>

      {error && <p className="vacio">{error}</p>}

      {r && !r.conductores.length && (
        <div className="tabla-caja">
          <p className="vacio">
            El camión <span className="num">{r.placa}</span> no tiene ningún viaje registrado
            alrededor de esa fecha. Puede que ese día no haya salido, o que el viaje esté a nombre
            de otra placa.
          </p>
        </div>
      )}

      {gana && (
        <>
          {/* La respuesta, en grande. Es a lo que vino. */}
          <div className="qm-respuesta">
            <p className="qm-et">
              {gana.eseMismoDia ? 'Ese mismo día manejaba' : 'El más cercano a esa fecha'}
            </p>
            {/* ⭐ Vulcano ya manda el nombre: medido el 24-ago-2026, 604 de 604. */}
            <p className="qm-nombre">{gana.nombre ?? 'Aún sin sincronizar'}</p>
            <p className="qm-ced num">{conPuntos(gana.cedula)}</p>
            <p className="qm-pie">
              {gana.eseMismoDia
                ? `Despachó ${cuantas(gana.remesas, 'viaje', 'viajes')} con esa placa, uno de ellos ese mismo día.`
                : `Su viaje más cercano fue ${gana.masCerca === 1 ? 'un día' : `${gana.masCerca} días`} antes o después. ` +
                  'Un viaje dura varios días, así que puede ser él.'}
            </p>
            {/* ⛔ Si hay más de uno, se dice. Dar un solo nombre cuando hubo dos
                sería hacer que alguien responda por lo que no hizo. */}
            {r.conductores.length > 1 && (
              <p className="qm-ojo">
                Ojo: esa placa la manejaron {r.conductores.length} personas en esos días.
                Míralos abajo antes de responder.
              </p>
            )}
          </div>

          <div className="tabla-caja">
            <table>
              <thead>
                <tr>
                  <th>Cuándo salió</th>
                  <th>Remesa</th>
                  <th>Quién manejaba</th>
                  <th>A dónde iba</th>
                  <th>Manifiesto</th>
                </tr>
              </thead>
              <tbody>
                {r.remesas.map((x) => (
                  <tr key={x.remesa} className={x.ese_mismo_dia ? 'qm-ese-dia' : undefined}>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>
                      {cuando(x.fecha_despacho)}
                      {x.ese_mismo_dia && <small>ese mismo día</small>}
                    </td>
                    <td className="num" style={{ fontWeight: 600 }}>
                      {x.remesa}
                    </td>
                    <td>
                      {x.conductor ?? '—'}
                      <small className="num">{conPuntos(x.conductor_cedula)}</small>
                    </td>
                    <td>{x.destino ?? 'Sin destino'}</td>
                    <td className="num">{x.numero_manifiesto ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}

/** «1 viaje», «3 viajes». */
function cuantas(n: number, una: string, varias: string): string {
  return `${n} ${n === 1 ? una : varias}`
}

function conPuntos(cedula: string): string {
  return cedula.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

export function Turnos() {
  const [d, setD] = useState<Datos | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [vista, setVista] = useState<'libres' | 'quien'>('libres')
  const [cerca, setCerca] = useState('')
  const [soloPropios, setSoloPropios] = useState<'' | 'PROPIO' | 'TERCERO'>('')

  const cargar = useCallback(async (queCerca: string) => {
    setError(null)
    try {
      setD(await pedirConSesion<Datos>(`/api/admin/turnos?cerca=${encodeURIComponent(queCerca)}`))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar')
    }
  }, [])

  useEffect(() => {
    void cargar(cerca)
  }, [cargar, cerca])

  /*
   * Clasificar sin salir de la pantalla.
   *
   * ⛔ Clasificar es poner una marca, no borrar nada: el camion sigue con toda
   * su historia y se puede volver a marcar cuando se quiera.
   */
  const [marcando, setMarcando] = useState<string | null>(null)
  async function marcar(id: string, propiedad: 'PROPIO' | 'TERCERO') {
    setMarcando(id)
    setError('')
    try {
      await pedirConSesion('/api/admin/clasificar/camiones', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ids: [id], propiedad }),
      })
      await cargar(cerca)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo marcar el camión')
    } finally {
      setMarcando(null)
    }
  }

  const n = (k: string) => Number(d?.resumen[k] ?? 0)
  const lista = (d?.libres ?? []).filter((x) => !soloPropios || x.propiedad === soloPropios)
  const pag = usarPaginas(lista)

  return (
    <>
      <div className="tit-fila">
        <div>
          <h1>Turnos</h1>
          <span>Qué camiones están libres, y quién manejaba cada uno en una fecha.</span>
        </div>
      </div>

      <div className="pestanas">
        <button
          className={`pestana ${vista === 'libres' ? 'pestana-on' : ''}`}
          onClick={() => setVista('libres')}
        >
          Quién está libre
        </button>
        {/* 🚨 Vive aquí porque es una pregunta sobre CAMIONES, y porque llega
            justo cuando alguien está mirando la flota: un comparendo, un
            reclamo. */}
        <button
          className={`pestana ${vista === 'quien' ? 'pestana-on' : ''}`}
          onClick={() => setVista('quien')}
        >
          ¿Quién manejaba?
        </button>
      </div>

      {vista === 'quien' && <QuienManejaba />}

      {vista === 'libres' && (
      <>
      <div className="kpis">
        <Kpi
          et="Libres ahora"
          v={n('libres')}
          pie={`Sin carga encima, o lo dijeron ellos · últimos ${d?.diasEnLaOperacion ?? 7} días`}
          resaltar={n('libres') > 0}
        />
        <Kpi et="Lo confirmaron" v={n('confirmados')} pie="Dijeron «estoy disponible»" />
        <Kpi et="En viaje" v={n('en_viaje')} pie="Con carga encima" />
        {/* Sin clasificar no es un problema del día, pero sí explica un número
            bajo de libres: un camión sin dueño asignado no se puede enturnar. */}
        <Kpi
          et="Sin clasificar"
          v={n('libres_sin_clasificar')}
          pie={`de los libres · ${n('sin_clasificar')} en toda la flota`}
        />
      </div>

      <div className="filtros-barra">
        <div className="campo ancho">
          <label htmlFor="t-cerca">¿Dónde necesitas el camión?</label>
          <select id="t-cerca" value={cerca} onChange={(e) => setCerca(e.target.value)}>
            <option value="">Cualquier parte — los que llevan más tiempo esperando</option>
            {(d?.destinos ?? []).map((x) => (
              <option key={x.codigo} value={x.codigo}>
                {x.nombre}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="t-quien">De quién</label>
          <select
            id="t-quien"
            value={soloPropios}
            onChange={(e) => setSoloPropios(e.target.value as '' | 'PROPIO' | 'TERCERO')}
          >
            <option value="">Todos</option>
            <option value="PROPIO">De la empresa</option>
            <option value="TERCERO">De terceros</option>
          </select>
        </div>
      </div>

      {error && <p className="vacio">{error}</p>}

      <div className="tabla-caja">
        <table>
          <thead>
            <tr>
              <th>Camión</th>
              <th>Conductor</th>
              <th>De quién</th>
              <th>Libre desde</th>
              <th>Dónde quedó</th>
              {d?.cerca && <th className="text-right">Distancia recta</th>}
            </tr>
          </thead>
          <tbody>
            {pag.enPantalla.map((x) => (
              <tr key={x.placa}>
                <td className="num" style={{ fontWeight: 600 }}>
                  {x.placa}
                </td>
                <td>
                  {/* ⭐ Ya llega de Vulcano; si falta, la cédula va debajo. */}
                  {x.conductor ?? <span style={{ color: '#8A94A6' }}>Aún sin sincronizar</span>}
                  {x.conductor_cedula && <small className="num">{x.conductor_cedula}</small>}
                </td>
                <td>
                  {/* Tres estados, no dos: un camion SIN CLASIFICAR no es un
                      tercero. Decir que lo es seria inventarle dueño. */}
                  {x.propiedad === 'PROPIO' ? (
                    <span className="et et-ok">De la empresa</span>
                  ) : x.propiedad === 'TERCERO' ? (
                    <>
                      <span className="et et-neu">Tercero</span>
                      {x.tercero && <small>{x.tercero}</small>}
                    </>
                  ) : (
                    /* ⭐ Se clasifica AQUI. La pila de «Por clasificar» tiene
                       cientos de placas y nadie se sienta a barrerla; estos
                       son los que estorban hoy, y son dos clics. */
                    <>
                      <span className="et et-esp">Sin clasificar</span>
                      <div className="fila-botones">
                        <button
                          className="boton boton-fila boton-primario"
                          disabled={marcando === x.id}
                          onClick={() => marcar(x.id, 'PROPIO')}
                        >
                          Es de la empresa
                        </button>
                        <button
                          className="boton boton-fila boton-secundario"
                          disabled={marcando === x.id}
                          onClick={() => marcar(x.id, 'TERCERO')}
                        >
                          Es de un tercero
                        </button>
                      </div>
                    </>
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {/* 🚨 Dos cosas distintas, y no se disfrazan de una sola:
                      el que lo DIJO tiene hora exacta; del que solo terminó su
                      viaje sabemos la fecha del viaje, no cuándo se desocupó.
                      Poner «libre hace 3 h» en el segundo sería inventarlo. */}
                  {x.confirmado ? (
                    <>
                      <span className="et et-ok">Lo confirmó</span>
                      <small className="num">{haceCuanto(x.horas_libre)}</small>
                      {/* Él dice que está libre y el sistema de la empresa le
                          sigue viendo carga. Se avisa: no se escoge por él. */}
                      {Number(x.encima ?? 0) > 0 && (
                        <small style={{ color: '#7d4a09' }}>
                          le figuran {Number(x.encima)} viajes abiertos
                        </small>
                      )}
                    </>
                  ) : (
                    <>
                      {x.dias_quieto === 0
                        ? 'Entregó hoy'
                        : x.dias_quieto === 1
                          ? 'Entregó ayer'
                          : `Hace ${x.dias_quieto} ${Number(x.dias_quieto) === 1 ? 'día' : 'días'}`}
                      <small className="num">último viaje {cuando(x.ultimo_viaje)}</small>
                    </>
                  )}
                </td>
                <td>
                  {x.donde ? (
                    <>
                      {x.donde}
                      {/* De dónde salió el punto. No se disfraza de GPS lo que
                          es el destino de su último viaje. */}
                      <small>
                        {x.fuenteUbicacion === 'GPS' ? 'por GPS' : 'donde entregó'}
                      </small>
                    </>
                  ) : (
                    /* Sin punto no se puede despachar por cercanía, y hay que
                       decirlo: es la diferencia entre «no hay» y «no sé». */
                    <span style={{ color: '#5A6A83' }}>Sin ubicación</span>
                  )}
                </td>
                {d?.cerca && (
                  <td className="num text-right">
                    {x.km != null ? `${Number(x.km)} km` : '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {lista.length === 0 && (
          <p className="vacio">
            {soloPropios
              ? 'Ningún camión libre de ese tipo. Quita el filtro «De quién» para ver todos.'
              : `Ningún camión está libre. Los ${n('en_viaje')} que hay en la calle llevan carga encima.`}
          </p>
        )}
        {lista.length > 0 && <PieDeTabla {...pag} queSon="camiones" />}
      </div>
      </>
      )}
    </>
  )
}

function Kpi({
  et,
  v,
  pie,
  resaltar,
  alerta,
}: {
  et: string
  v: number
  pie: string
  resaltar?: boolean
  alerta?: boolean
}) {
  return (
    <div className="kpi">
      <p className="kpi-et">{et}</p>
      <p
        className="kpi-n num"
        style={alerta ? { color: 'var(--color-ambar)' } : resaltar ? { color: 'var(--color-verde)' } : undefined}
      >
        {v}
      </p>
      <p className="kpi-pie">{pie}</p>
    </div>
  )
}

/** «hace 3 h», «hace 2 días». Un turno se mide en espera, no en fecha. */
function haceCuanto(horas: number | null): string {
  if (horas == null) return '—'
  if (horas < 1) return 'hace menos de 1 h'
  if (horas < 48) return `hace ${horas} h`
  return `hace ${Math.round(horas / 24)} días`
}

function cuando(iso: string | null): string {
  if (!iso) return ''
  const f = new Date(iso.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'))
  if (Number.isNaN(f.getTime())) return ''
  return f.toLocaleString('es-CO', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}
